import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Wind, Play, Pause, Square, Settings } from 'lucide-react';
import BreathingCircle from './components/BreathingCircle';
import ControlPanel from './components/ControlPanel';
import SettingsPanel from './components/SettingsPanel';
import SessionSummary from './components/SessionSummary';

/**
 * Breath & Timing Coach Tool
 * Production-ready breathing exercise guide for musical theatre training
 * Uses requestAnimationFrame for smooth, accurate timing
 */
export default function BreathTimingCoachTool() {
  // Breathing phases
  const PHASES = {
    INHALE: 'inhale',
    HOLD_IN: 'hold_in',
    EXHALE: 'exhale',
    HOLD_OUT: 'hold_out'
  };

  // State management
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(PHASES?.INHALE);
  const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(4);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  
  // Phase durations (in seconds)
  const [phaseDurations, setPhaseDurations] = useState({
    inhale: 4,
    hold_in: 4,
    exhale: 4,
    hold_out: 4
  });

  // Session settings
  const [settings, setSettings] = useState({
    totalCycles: Infinity, // Infinity for continuous
    enableBeeps: false,
    enableMetronome: false,
    enableVibration: false,
    lowMotion: false,
    bpm: 60,
    beatsPerPhase: 4
  });

  // Session data
  const [sessionData, setSessionData] = useState({
    startTime: null,
    endTime: null,
    completedCycles: 0,
    totalDuration: 0,
    pauseCount: 0
  });

  // Animation refs
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const pauseTimeRef = useRef(null);
  const audioContextRef = useRef(null);

  // Phase copy
  const phaseCopy = {
    [PHASES?.INHALE]: { label: 'INHALE', message: 'Fill the tank…', color: 'from-blue-400 to-cyan-500' },
    [PHASES?.HOLD_IN]: { label: 'HOLD', message: 'Keep it calm…', color: 'from-cyan-500 to-teal-500' },
    [PHASES?.EXHALE]: { label: 'EXHALE', message: 'Release the sound…', color: 'from-pink-400 to-rose-500' },
    [PHASES?.HOLD_OUT]: { label: 'HOLD', message: 'Reset and prep…', color: 'from-purple-400 to-indigo-500' }
  };

  // Presets
  const presets = [
    { name: '4-4-4-4 (Default)', values: { inhale: 4, hold_in: 4, exhale: 4, hold_out: 4 } },
    { name: 'Box Breathing (4-4-4-4)', values: { inhale: 4, hold_in: 4, exhale: 4, hold_out: 4 } },
    { name: 'Quick Reset (3-3-3-3)', values: { inhale: 3, hold_in: 3, exhale: 3, hold_out: 3 } },
    { name: 'Performance Calm (4-7-8-0)', values: { inhale: 4, hold_in: 7, exhale: 8, hold_out: 0 } }
  ];

  // Initialize Audio Context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef?.current) {
        audioContextRef?.current?.close();
      }
    };
  }, []);

  // Main animation loop using requestAnimationFrame
  const animate = useCallback((timestamp) => {
    if (!startTimeRef?.current) {
      startTimeRef.current = timestamp;
    }

    const elapsed = (timestamp - startTimeRef?.current) / 1000; // Convert to seconds
    const currentPhaseDuration = phaseDurations?.[currentPhase];
    const remaining = Math.max(0, currentPhaseDuration - elapsed);

    setPhaseTimeRemaining(remaining);

    // Phase transition
    if (remaining <= 0) {
      transitionToNextPhase();
      startTimeRef.current = timestamp;
    }

    if (isActive && !isPaused) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [isActive, isPaused, currentPhase, phaseDurations]);

  // Start animation loop
  useEffect(() => {
    if (isActive && !isPaused) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef?.current) {
        cancelAnimationFrame(animationFrameRef?.current);
      }
    };
  }, [isActive, isPaused, animate]);

  // Transition to next phase
  const transitionToNextPhase = () => {
    const phaseOrder = [PHASES?.INHALE, PHASES?.HOLD_IN, PHASES?.EXHALE, PHASES?.HOLD_OUT];
    const currentIndex = phaseOrder?.indexOf(currentPhase);
    const nextIndex = (currentIndex + 1) % phaseOrder?.length;
    const nextPhase = phaseOrder?.[nextIndex];

    // Play beep on transition
    if (settings?.enableBeeps) {
      playBeep();
    }

    // Vibrate on mobile
    if (settings?.enableVibration && 'vibrate' in navigator) {
      navigator.vibrate(100);
    }

    // Complete cycle when returning to inhale
    if (nextPhase === PHASES?.INHALE) {
      const newCycle = currentCycle + 1;
      setCurrentCycle(newCycle);
      setSessionData(prev => ({ ...prev, completedCycles: newCycle }));

      // Check if reached target cycles
      if (settings?.totalCycles !== Infinity && newCycle >= settings?.totalCycles) {
        handleStop();
        return;
      }
    }

    setCurrentPhase(nextPhase);
  };

  // Play beep sound
  const playBeep = () => {
    if (!audioContextRef?.current) return;

    const oscillator = audioContextRef?.current?.createOscillator();
    const gainNode = audioContextRef?.current?.createGain();

    oscillator?.connect(gainNode);
    gainNode?.connect(audioContextRef?.current?.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode?.gain?.setValueAtTime(0.3, audioContextRef?.current?.currentTime);
    gainNode?.gain?.exponentialRampToValueAtTime(0.01, audioContextRef?.current?.currentTime + 0.1);

    oscillator?.start(audioContextRef?.current?.currentTime);
    oscillator?.stop(audioContextRef?.current?.currentTime + 0.1);
  };

  // Start breathing session
  const handleStart = () => {
    if (!isActive) {
      // New session
      setIsActive(true);
      setIsPaused(false);
      setCurrentPhase(PHASES?.INHALE);
      setPhaseTimeRemaining(phaseDurations?.inhale);
      setCurrentCycle(0);
      setSessionData({
        startTime: Date.now(),
        endTime: null,
        completedCycles: 0,
        totalDuration: 0,
        pauseCount: 0
      });
      startTimeRef.current = null;
    } else if (isPaused) {
      // Resume
      setIsPaused(false);
      startTimeRef.current = null;
    }
  };

  // Pause session
  const handlePause = () => {
    setIsPaused(true);
    setSessionData(prev => ({ ...prev, pauseCount: prev?.pauseCount + 1 }));
    pauseTimeRef.current = Date.now();
  };

  // Stop session
  const handleStop = () => {
    setIsActive(false);
    setIsPaused(false);
    const endTime = Date.now();
    const duration = (endTime - sessionData?.startTime) / 1000;
    
    setSessionData(prev => ({
      ...prev,
      endTime,
      totalDuration: duration
    }));

    setShowSummary(true);
  };

  // Reset session
  const handleReset = () => {
    setShowSummary(false);
    setIsActive(false);
    setIsPaused(false);
    setCurrentPhase(PHASES?.INHALE);
    setPhaseTimeRemaining(phaseDurations?.inhale);
    setCurrentCycle(0);
    startTimeRef.current = null;
  };

  // Apply preset
  const applyPreset = (preset) => {
    setPhaseDurations(preset?.values);
    setPhaseTimeRemaining(preset?.values?.inhale);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-500/30 rounded-xl">
                <Wind className="w-8 h-8 text-cyan-200" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  🎭 Breath & Timing Coach
                </h1>
                <p className="text-white/80">Build control for singing and stage performance</p>
              </div>
            </div>

            {/* Settings Button */}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <Settings className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Safety Note */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
          <p className="text-amber-200 text-sm text-center">
            ⚠️ If you feel dizzy, stop and breathe normally.
          </p>
        </div>

        {/* Main Coach Panel */}
        {!showSummary ? (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: Breathing Circle & Phase Info */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              {/* Phase Label */}
              <div className="text-center mb-6">
                <div className={`inline-block bg-gradient-to-r ${phaseCopy?.[currentPhase]?.color} px-8 py-3 rounded-full shadow-lg`}>
                  <h2 className="text-3xl font-bold text-white">
                    {phaseCopy?.[currentPhase]?.label}
                  </h2>
                </div>
                <p className="text-white/80 text-lg mt-4 italic">
                  {phaseCopy?.[currentPhase]?.message}
                </p>
              </div>

              {/* Breathing Circle */}
              <BreathingCircle
                phase={currentPhase}
                progress={1 - (phaseTimeRemaining / phaseDurations?.[currentPhase])}
                isActive={isActive && !isPaused}
                lowMotion={settings?.lowMotion}
              />

              {/* Timer Display */}
              <div className="text-center mt-6">
                <div className="text-6xl font-bold text-white">
                  {phaseTimeRemaining?.toFixed(1)}s
                </div>
                <div className="text-white/60 mt-2">
                  Cycle {currentCycle + 1}
                  {settings?.totalCycles !== Infinity && ` / ${settings?.totalCycles}`}
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex justify-center gap-4 mt-8">
                {!isActive && (
                  <button
                    onClick={handleStart}
                    className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all transform hover:scale-105"
                  >
                    <Play className="w-6 h-6" />
                    🎤 Warm up my breath!
                  </button>
                )}

                {isActive && !isPaused && (
                  <>
                    <button
                      onClick={handlePause}
                      className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-bold transition-all"
                    >
                      <Pause className="w-5 h-5" />
                      Pause
                    </button>
                    <button
                      onClick={handleStop}
                      className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-bold transition-all"
                    >
                      <Square className="w-5 h-5" />
                      Stop
                    </button>
                  </>
                )}

                {isActive && isPaused && (
                  <>
                    <button
                      onClick={handleStart}
                      className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-bold transition-all"
                    >
                      <Play className="w-5 h-5" />
                      Resume
                    </button>
                    <button
                      onClick={handleStop}
                      className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-bold transition-all"
                    >
                      <Square className="w-5 h-5" />
                      Stop
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Right: Controls & Settings */}
            <div className="space-y-6">
              <ControlPanel
                phaseDurations={phaseDurations}
                setPhaseDurations={setPhaseDurations}
                settings={settings}
                setSettings={setSettings}
                presets={presets}
                onApplyPreset={applyPreset}
                disabled={isActive}
              />
            </div>
          </div>
        ) : (
          <SessionSummary
            sessionData={sessionData}
            phaseDurations={phaseDurations}
            onRestart={handleReset}
          />
        )}

        {/* Settings Modal */}
        {showSettings && (
          <SettingsPanel
            settings={settings}
            setSettings={setSettings}
            onClose={() => setShowSettings(false)}
          />
        )}
      </div>
    </div>
  );
}